/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { Suspense, useRef } from 'react';
import { Await, type LoaderFunctionArgs, useRouteLoaderData } from 'react-router';
import { getConfig } from '@salesforce/storefront-next-runtime/config';
import { isDesignModeActive, isPreviewModeActive } from '@salesforce/storefront-next-runtime/design/mode';
import { type ShopperProducts } from '@salesforce/storefront-next-runtime/scapi';
import { fetchCategory, fetchCategoriesByIds } from '@/lib/api/categories.server';
import { fetchMenuPagesMap } from '@/lib/api/menu.server';
import type { MenuLoaderResult } from '@/routes/resource.menu';
import { fetchPageWithComponentData, type PageWithComponentData } from '@/lib/util/pageLoader.server';
import { fetchContentAsset, type ContentAsset } from '@/lib/api/content.server';
import type { loader as rootLoader } from '@/root';
import { getLogger } from '@/lib/logger.server';
import type { AppConfig } from '@/types/config';
import Header from '@/components/header';
import Footer from '@/components/footer';
import ResponsiveNavigationMenu from '@/components/navigation-menu-mega';
import PromoBar, { extractPromoBarContent, extractHeaderHelpContent } from '@/components/promo-bar';
import PendingNavigationOutlet from '@/components/pending-navigation-outlet';
import { resolveCategoryPendingNavigation } from '@/components/category-pending-shell/navigation-policy';
/**
 * Page Designer page that owns the storefront logo. Configured in Business
 * Manager and rendered into the header on every `_app.*` route via
 * `<HeaderLogo>`. If unavailable, the bundled `/images/logo.svg` is used.
 */
const HEADER_LOGO_PAGE_ID = 'header-logo-fj';
const GLOBAL_FOOTER_PAGE_ID = 'global-footer';
/** Content asset driving the header "Help" trigger and its panel of links. */
const HEADER_HELP_CONTENT_ASSET_ID = 'header-help';

/**
 * Extracts a named region from the FooterLayout component on the consolidated
 * `global-footer` PD page and wraps it as a standalone PageWithComponentData.
 * This lets all existing footer extraction functions work unchanged — each
 * receives a fake page whose `regions` array contains only the one slot they
 * care about.
 */
function getFooterRegionAsPage(
    footerPage: PageWithComponentData | null,
    regionId: string
): PageWithComponentData | null {
    if (!footerPage) return null;
    for (const topRegion of footerPage.regions ?? []) {
        for (const comp of topRegion.components ?? []) {
            if (comp.typeId === 'Layout.footerLayout' && comp.regions) {
                const nestedRegion = comp.regions.find((r) => r.id === regionId);
                if (nestedRegion) {
                    return { ...footerPage, regions: [nestedRegion] };
                }
            }
        }
    }
    return null;
}

type LoaderData = {
    root: Promise<ShopperProducts.schemas['Category']>;
    subs: Promise<ShopperProducts.schemas['Category'][]>;
    menuPages: Promise<MenuLoaderResult>;
    promoBarData: Promise<{ page: PageWithComponentData | null; helpAsset: ContentAsset | null }>;
    headerLogoPage: Promise<PageWithComponentData | null>;
    footerSocialPage: Promise<PageWithComponentData | null>;
    smsSignupPage: Promise<PageWithComponentData | null>;
    emailSignupPage: Promise<PageWithComponentData | null>;
    siteLinksPage: Promise<PageWithComponentData | null>;
    legalLinksPage: Promise<PageWithComponentData | null>;
    helpPage: Promise<PageWithComponentData | null>;
    copyrightPage: Promise<PageWithComponentData | null>;
    footerInsiderGuestPage: Promise<PageWithComponentData | null>;
    footerInsiderLoggedInPage: Promise<PageWithComponentData | null>;
    footerLogoPage: Promise<PageWithComponentData | null>;
};

/**
 * We're using the `shouldRevalidate` functionality to only load the navigation menu categories from the server on the
 * very first navigation to a page using the (default) `_app` layout. Those categories requests are divided into two
 * discrete request phases; the first to only fetch the root categories, and subsequent multiple calls to fetch the
 * identified sub categories to be displayed in the navigation menu. This behavior ensures that the initial data
 * doesn't get overwritten/removed on subsequent client-side navigations.
 *
 * **Note:** If there is a desire/intent to customize that behavior, the `shouldRevalidate` function can be modified
 * accordingly. For example, a client-side storage for the categories could be introduced to implement any desired
 * caching strategy, including specific reload/refresh intervals/conditions.
 * @see {@link https://reactrouter.com/start/framework/route-module#shouldrevalidate}
 */
export function shouldRevalidate() {
    return false;
}

export function loader(args: LoaderFunctionArgs): LoaderData {
    const { context, request } = args;

    // In Page Designer (edit/preview mode), skip all nav and chrome fetches — header
    // and footer are not rendered in the BM canvas, so there is no need to load them.
    if (isDesignModeActive(request) || isPreviewModeActive(request)) {
        const empty = Promise.resolve(null);
        return {
            root: Promise.resolve({} as ShopperProducts.schemas['Category']),
            subs: Promise.resolve([]),
            menuPages: Promise.resolve({} as MenuLoaderResult),
            promoBarData: Promise.resolve({ page: null, helpAsset: null }),
            headerLogoPage: empty,
            footerSocialPage: empty,
            smsSignupPage: empty,
            emailSignupPage: empty,
            siteLinksPage: empty,
            legalLinksPage: empty,
            helpPage: empty,
            copyrightPage: empty,
            footerInsiderGuestPage: empty,
            footerInsiderLoggedInPage: empty,
            footerLogoPage: empty,
        };
    }

    const logger = getLogger(context);
    const config = getConfig<AppConfig>(context);
    const { rootCategoryId, maxDepth } = config.pages.navigation;
    // Opting these nav-category and menu-page fetches out of personalization makes their
    // responses eligible for the SCAPI CDN cache. Gated behind a flag (default off) because
    // whether this content is actually shopper-independent depends on PD visibility rules,
    // customer groups, and component loaders configured in Business Manager.
    const cdnEligible = config.features.cdnEligibility?.enabled ?? false;
    const personalized = cdnEligible ? ('none' as const) : undefined;

    logger.debug('AppLayout: loader starting', { rootCategoryId, maxDepth, cdnEligible });

    // Load the root category and its sub categories information
    // Depth 1 fetches the root category with its immediate children
    const rootCategoryPromise = fetchCategory(context, rootCategoryId, 1, personalized);

    // Load each second-level sub categories tree as well, in case the resolved root-level category has any sub
    // categories and maxDepth allows for it. We then base this composed second-level promise on the initial root
    // category promise to allow for parallel loading and streaming of the two main promises.
    // All eligible category IDs are fetched in a single batched getCategories request (up to 50 IDs per call)
    // rather than one getCategory call per category, reducing the request fan-out to a constant 1 call.
    const subCategoriesPromise =
        maxDepth >= 2
            ? rootCategoryPromise.then((rootCategory: ShopperProducts.schemas['Category']) => {
                  const navCategories = (rootCategory.categories ?? []).filter(
                      (cat) => (cat as Record<string, unknown>).c_showInMenu === true
                  );
                  const idsToFetch = navCategories
                      .filter(
                          (subCategory) =>
                              typeof subCategory.onlineSubCategoriesCount === 'number' &&
                              subCategory.onlineSubCategoriesCount > 0
                      )
                      .map((subCategory) => subCategory.id);
                  return fetchCategoriesByIds(context, idsToFetch, maxDepth as 0 | 1 | 2, personalized);
              })
            : Promise.resolve([]);

    // Menu pages: one PD page fetch per nav category, all in parallel, chained from
    // the root-category promise (so we know which categories are in the nav).
    // Returned unresolved so it doesn't block the initial HTML response — the browser
    // receives the data via HTTP streaming. No client-side fetch needed.
    const menuPagesPromise: Promise<MenuLoaderResult> = rootCategoryPromise
        .then((rootCategory: ShopperProducts.schemas['Category']) => {
            const navCategories = (rootCategory.categories ?? []).filter(
                (cat: ShopperProducts.schemas['Category']) =>
                    (cat as Record<string, unknown>).c_showInMenu === true
            );
            const pagesMap = fetchMenuPagesMap(args, navCategories, { personalized });
            return Promise.all(
                Object.entries(pagesMap).map(async ([id, p]) => [id, await p])
            ).then((entries) => Object.fromEntries(entries) as MenuLoaderResult);
        })
        .catch((err: unknown) => {
            logger.warn('AppLayout: failed to load menu pages', { err });
            return {} as MenuLoaderResult;
        });

    const promoBarPagePromise = fetchPageWithComponentData(args, { pageId: 'global-header' }).catch((err: unknown) => {
        logger.warn('AppLayout: failed to load global-header promo bar page', { err });
        return null;
    });

    const headerHelpAssetPromise = fetchContentAsset(context, HEADER_HELP_CONTENT_ASSET_ID).catch((err: unknown) => {
        logger.warn('AppLayout: failed to load header-help content asset', { err });
        return null;
    });

    const promoBarDataPromise = Promise.all([promoBarPagePromise, headerHelpAssetPromise]).then(([page, helpAsset]) => ({
        page,
        helpAsset,
    }));

    const headerLogoPagePromise = fetchPageWithComponentData(args, { pageId: HEADER_LOGO_PAGE_ID }).catch(
        (err: unknown) => {
            logger.warn('AppLayout: failed to load header logo page', { err });
            return null;
        }
    );

    // Single consolidated fetch replacing all individual footer page calls.
    // The global-footer PD page contains a FooterLayout component with named
    // regions for each footer section. Per-region promises are derived from
    // this one fetch so the Footer component interface stays unchanged.
    const globalFooterPagePromise = fetchPageWithComponentData(args, { pageId: GLOBAL_FOOTER_PAGE_ID }).catch(
        (err: unknown) => {
            logger.warn('AppLayout: failed to load global-footer page', { err });
            return null;
        }
    );

    const footerSocialPagePromise = globalFooterPagePromise.then((p) => getFooterRegionAsPage(p, 'social'));
    const footerInsiderGuestPagePromise = globalFooterPagePromise.then((p) => getFooterRegionAsPage(p, 'insider'));
    const footerInsiderLoggedInPagePromise = globalFooterPagePromise.then((p) =>
        getFooterRegionAsPage(p, 'insiderLoggedIn')
    );
    const footerLogoPagePromise = globalFooterPagePromise.then((p) => getFooterRegionAsPage(p, 'logo'));
    const legalLinksPagePromise = globalFooterPagePromise.then((p) => getFooterRegionAsPage(p, 'legal'));
    const siteLinksPagePromise = globalFooterPagePromise.then((p) => getFooterRegionAsPage(p, 'links'));
    const helpPagePromise = globalFooterPagePromise.then((p) => getFooterRegionAsPage(p, 'help'));
    const copyrightPagePromise = globalFooterPagePromise.then((p) => getFooterRegionAsPage(p, 'copy'));
    const smsSignupPagePromise = globalFooterPagePromise.then((p) => getFooterRegionAsPage(p, 'smsSignup'));
    const emailSignupPagePromise = globalFooterPagePromise.then((p) => getFooterRegionAsPage(p, 'emailSignup'));

    return {
        root: rootCategoryPromise,
        subs: subCategoriesPromise,
        menuPages: menuPagesPromise,
        promoBarData: promoBarDataPromise,
        headerLogoPage: headerLogoPagePromise,
        footerSocialPage: footerSocialPagePromise,
        smsSignupPage: smsSignupPagePromise,
        emailSignupPage: emailSignupPagePromise,
        legalLinksPage: legalLinksPagePromise,
        siteLinksPage: siteLinksPagePromise,
        helpPage: helpPagePromise,
        copyrightPage: copyrightPagePromise,
        footerInsiderGuestPage: footerInsiderGuestPagePromise,
        footerInsiderLoggedInPage: footerInsiderLoggedInPagePromise,
        footerLogoPage: footerLogoPagePromise,
    };
}

/**
 * Default Layout Route
 *
 * This pathless layout route provides the standard storefront UI structure:
 * - Header with navigation
 * - Main content area (via `<Outlet/>`)
 * - Footer
 *
 * Routes that need this layout should be prefixed with `_app.` in their filename.
 * For routes without default header/footer (e.g., login), use the `_empty.` prefix instead.
 */
export default function DefaultLayout({
    loaderData: {
        root,
        subs,
        menuPages,
        headerLogoPage,
        promoBarData,
        footerSocialPage,
        smsSignupPage,
        emailSignupPage,
        legalLinksPage,
        siteLinksPage,
        helpPage,
        copyrightPage,
        footerInsiderGuestPage,
        footerInsiderLoggedInPage,
        footerLogoPage,
    },
}: {
    loaderData: LoaderData;
}) {
    const rootData = useRouteLoaderData<typeof rootLoader>('root');
    const isPageDesignerMode = !!rootData?.pageDesignerMode;

    const refRoot = useRef<Promise<ShopperProducts.schemas['Category']> | undefined>(undefined);
    const refSubs = useRef<Promise<ShopperProducts.schemas['Category'][]> | undefined>(undefined);
    const refMenuPages = useRef<Promise<MenuLoaderResult> | undefined>(undefined);
    if (!refRoot.current && !refSubs.current) {
        refRoot.current = root;
        refSubs.current = subs;
        refMenuPages.current = menuPages;
    }

    if (isPageDesignerMode) {
        return (
            <main className="grow">
                <PendingNavigationOutlet resolvePending={resolveCategoryPendingNavigation} />
            </main>
        );
    }

    return (
        <>
            <div className="sticky top-0 z-50 flex flex-col">
                <Suspense fallback={<PromoBar />}>
                    <Await resolve={promoBarData} errorElement={<PromoBar />}>
                        {(
                            data:
                                | { page: PageWithComponentData | null; helpAsset: ContentAsset | null }
                                | undefined
                        ) => {
                            const { page, helpAsset } = data ?? { page: null, helpAsset: null };
                            const { slides, utilityLinks } = extractPromoBarContent(page);
                            const helpMenu = extractHeaderHelpContent(helpAsset);
                            return (
                                <PromoBar
                                    slides={slides.length > 0 ? slides : undefined}
                                    utilityLinks={utilityLinks.length > 0 ? utilityLinks : undefined}
                                    helpMenu={helpMenu}
                                />
                            );
                        }}
                    </Await>
                </Suspense>
                <div className="order-2">
                    <Header logoPage={headerLogoPage}>
                        <ResponsiveNavigationMenu
                                resolve={refRoot.current}
                                defer={refSubs.current}
                                menuPages={refMenuPages.current}
                            />
                    </Header>
                </div>
            </div>
            <main className="grow pt-6">
                <PendingNavigationOutlet resolvePending={resolveCategoryPendingNavigation} />
            </main>
            <Footer
                categories={refRoot.current}
                socialPage={footerSocialPage}
                smsSignupPage={smsSignupPage}
                emailSignupPage={emailSignupPage}
                legalLinksPage={legalLinksPage}
                siteLinksPage={siteLinksPage}
                helpPage={helpPage}
                copyrightPage={copyrightPage}
                footerInsiderGuestPage={footerInsiderGuestPage}
                footerInsiderLoggedInPage={footerInsiderLoggedInPage}
                logoPage={footerLogoPage}
            />
        </>
    );
}
